import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminChatUsage from './AdminChatUsage';
import { API_URL } from './App';
import { 
  Search, User, Mail, Shield, Crown, CheckCircle, XCircle, 
  MessageSquare, Image, Video, BarChart3, Users, Activity,
  Download, Eye, Trash2, Filter, Calendar, Clock, ChevronDown,
  ChevronUp, MoreVertical, Settings, RefreshCw, AlertTriangle,
  FileText, TrendingUp, Zap, Award, Lock, Unlock, ThumbsUp,
  ThumbsDown, Globe, Smartphone, Laptop, Server, MessageCircle, HardDrive
} from 'lucide-react';

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userChats, setUserChats] = useState([]);
  const [userImages, setUserImages] = useState([]);
  const [userVideos, setUserVideos] = useState([]);
  const [stats, setStats] = useState(null);
  const [moderationQueue, setModerationQueue] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [usageAnalytics, setUsageAnalytics] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [period, setPeriod] = useState('7d');
  const [expandedUser, setExpandedUser] = useState(null);
  const [expandedChat, setExpandedChat] = useState(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    } else if (activeTab === 'moderation') {
      fetchModerationQueue();
    } else if (activeTab === 'activity') {
      fetchActivityLogs();
    } else if (activeTab === 'analytics') {
      fetchUsageAnalytics();
    }
  }, [activeTab, page, search, period]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/users`, {
        params: { page, search, limit: 20 },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setMessage('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

const fetchUserDetails = async (userId) => {
  try {
    const response = await axios.get(`${API_URL}/admin/users/${userId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    
    if (response.data.success) {
      // The API already returns stats with actual counts from content tables
      setUserDetails(response.data.user);
    }
  } catch (error) {
    console.error('Failed to fetch user details:', error);
  }
};


  const fetchUserChats = async (userId, pageNum = 1) => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}/chats`, {
        params: { page: pageNum, limit: 10 },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUserChats(response.data.chats);
      }
    } catch (error) {
      console.error('Failed to fetch user chats:', error);
    }
  };

  const fetchUserImages = async (userId, pageNum = 1) => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}/images`, {
        params: { page: pageNum, limit: 10 },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUserImages(response.data.images);
      }
    } catch (error) {
      console.error('Failed to fetch user images:', error);
    }
  };

  const fetchUserVideos = async (userId, pageNum = 1) => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}/videos`, {
        params: { page: pageNum, limit: 10 },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUserVideos(response.data.videos);
      }
    } catch (error) {
      console.error('Failed to fetch user videos:', error);
    }
  };

  const fetchModerationQueue = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/moderation/queue`, {
        params: { page, limit: 20 },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setModerationQueue(response.data.queue);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (error) {
      console.error('Failed to fetch moderation queue:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/activity-log`, {
        params: { page, limit: 50 },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setActivityLogs(response.data.logs);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/analytics/usage`, {
        params: { period },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUsageAnalytics(response.data.analytics);
      }
    } catch (error) {
      console.error('Failed to fetch usage analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMakePremium = async (userId) => {
    if (!window.confirm('Make this user premium? This will give them unlimited access.')) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/admin/users/${userId}/make-premium`,
        { plan: 'paid', notes: 'Manually upgraded via admin dashboard' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setMessage(`User upgraded to premium successfully`);
        fetchUsers();
        if (selectedUser === userId) {
          fetchUserDetails(userId);
        }
      }
    } catch (error) {
      console.error('Failed to upgrade user:', error);
      setMessage('Failed to upgrade user');
    }
  };

  const handleVerifyEmail = async (userId) => {
    if (!window.confirm('Manually verify this user\'s email?')) return;
    
    try {
      const response = await axios.post(
        `${API_URL}/admin/users/${userId}/verify-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setMessage(`User email verified successfully`);
        fetchUsers();
        if (selectedUser === userId) {
          fetchUserDetails(userId);
        }
      }
    } catch (error) {
      console.error('Failed to verify email:', error);
      setMessage('Failed to verify email');
    }
  };

  const handleDeleteContent = async (userId, type, contentId) => {
    if (!window.confirm(`Delete this ${type}? This action cannot be undone.`)) return;
    
    try {
      const response = await axios.delete(
        `${API_URL}/admin/users/${userId}/content/${type}/${contentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setMessage(`${type} deleted successfully`);
        if (type === 'chat') {
          fetchUserChats(userId);
        } else if (type === 'image') {
          fetchUserImages(userId);
        } else if (type === 'video') {
          fetchUserVideos(userId);
        }
      }
    } catch (error) {
      console.error('Failed to delete content:', error);
      setMessage('Failed to delete content');
    }
  };

  const handleModerateImage = async (imageId, approved) => {
    try {
      const response = await axios.post(
        `${API_URL}/admin/content/image/${imageId}/moderate`,
        { 
          approved, 
          reason: approved ? 'Approved by admin' : 'Rejected by admin' 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setMessage(`Image ${approved ? 'approved' : 'rejected'}`);
        fetchModerationQueue();
      }
    } catch (error) {
      console.error('Failed to moderate image:', error);
      setMessage('Failed to moderate image');
    }
  };

  const handleExportUsers = async (format = 'json') => {
    try {
      const response = await axios.get(`${API_URL}/admin/export/users`, {
        params: { format },
        headers: { Authorization: `Bearer ${token}` },
        responseType: format === 'csv' ? 'blob' : 'json'
      });
      
      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `users_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
        const exportFileDefaultName = `users_export_${new Date().toISOString().split('T')[0]}.json`;
        const linkElement = document.createElement('a');
        linkElement.setAttribute('href', dataUri);
        linkElement.setAttribute('download', exportFileDefaultName);
        linkElement.click();
      }
      
      setMessage(`Users exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Failed to export users:', error);
      setMessage('Failed to export users');
    }
  };

  const toggleUserExpand = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setUserDetails(null);
      setUserChats([]);
      setUserImages([]);
      setUserVideos([]);
    } else {
      setExpandedUser(userId);
      setSelectedUser(userId);
      await fetchUserDetails(userId);
      await fetchUserChats(userId);
      await fetchUserImages(userId);
      await fetchUserVideos(userId);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffSeconds = Math.floor((now - date) / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    if (diffMinutes > 0) return `${diffMinutes}m ago`;
    return `${diffSeconds}s ago`;
  };

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">System administration and user management</p>
          </div>
          
          <div className="mt-4 md:mt-0 flex space-x-3">
            <button
              onClick={() => handleExportUsers('json')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export JSON</span>
            </button>
            <button
              onClick={() => handleExportUsers('csv')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              <FileText className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>

        {message && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg flex items-center justify-between">
            <p className="text-green-400">{message}</p>
            <button onClick={() => setMessage('')} className="text-green-400 hover:text-green-300">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Users</p>
                  <p className="text-3xl font-bold text-white">{stats.users.total_users}</p>
                  <p className="text-xs text-green-400 mt-1">+{stats.users.new_users_week} this week</p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-lg">
                  <Users className="w-6 h-6 text-purple-400" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-xs text-gray-400">
                <Crown className="w-3 h-3 mr-1 text-yellow-400" />
                <span>{stats.users.premium_users} Premium</span>
                <CheckCircle className="w-3 h-3 ml-3 mr-1 text-green-400" />
                <span>{stats.users.verified_users} Verified</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Total Generations</p>
                  <p className="text-3xl font-bold text-white">
                    {stats.content.total_images_generated + stats.content.total_videos_generated}
                  </p>
                  <p className="text-xs text-blue-400 mt-1">{stats.content.chats_last_24h} chats today</p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-lg">
                  <Zap className="w-6 h-6 text-blue-400" />
                </div>
              </div>
              <div className="mt-4 flex items-center text-xs text-gray-400">
                <Image className="w-3 h-3 mr-1" />
                <span>{stats.content.total_images_generated} Images</span>
                <Video className="w-3 h-3 ml-3 mr-1" />
                <span>{stats.content.total_videos_generated} Videos</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Chat Sessions</p>
                  <p className="text-3xl font-bold text-white">{stats.content.total_chat_sessions}</p>
                  <p className="text-xs text-green-400 mt-1">{stats.content.chats_last_24h} last 24h</p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-green-400" />
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-400">
                Avg {Math.round(stats.users.avg_chats_per_user || 0)} chats per user
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">System Health</p>
                  <p className="text-3xl font-bold text-white">Active</p>
                  <p className="text-xs text-green-400 mt-1">All systems operational</p>
                </div>
                <div className="p-3 bg-orange-500/20 rounded-lg">
                  <Server className="w-6 h-6 text-orange-400" />
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-400">
                {stats.users.admin_users} Admin users
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-gray-800/50 p-1 rounded-lg border border-gray-700">
          <button
            onClick={() => setActiveTab('users')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'users' 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Users</span>
          </button>
          <button
  onClick={() => setActiveTab('chat-usage')}
  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
    activeTab === 'chat-usage' 
      ? 'bg-purple-600 text-white' 
      : 'text-gray-400 hover:text-white hover:bg-gray-700'
  }`}
>
  <MessageCircle className="w-4 h-4" />
  <span>Chat Usage</span>
</button>
          <button
            onClick={() => setActiveTab('moderation')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'moderation' 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <Shield className="w-4 h-4" />
            <span>Moderation</span>
            {moderationQueue.length > 0 && (
              <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                {moderationQueue.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'analytics' 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Analytics</span>
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              activeTab === 'activity' 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            <Activity className="w-4 h-4" />
            <span>Activity Log</span>
          </button>
        </div>

        {/* Search Bar (only for users tab) */}
        {activeTab === 'users' && (
          <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by email or user ID..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500 text-white"
                />
              </div>
              <button
                onClick={fetchUsers}
                className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        )}

        {/* Period Selector (for analytics tab) */}
        {activeTab === 'analytics' && (
          <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6 mb-6">
            <div className="flex items-center space-x-4">
              <Filter className="w-5 h-5 text-gray-500" />
              <span className="text-white">Time Period:</span>
              <div className="flex space-x-2">
                {['24h', '7d', '30d', '12m'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setPeriod(p)}
                    className={`px-4 py-2 rounded-lg transition-colors ${
                      period === p 
                        ? 'bg-purple-600 text-white' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {p === '24h' ? '24 Hours' : p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : '12 Months'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Content based on active tab */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-400 border-t-transparent"></div>
            <p className="text-gray-400 mt-4">Loading...</p>
          </div>
        ) : (
          <>
            {/* USERS TAB */}
            {activeTab === 'users' && (
              <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700 bg-gray-900/50">
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">User</th>
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Status</th>
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Plan</th>
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Usage</th>
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Joined</th>
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <React.Fragment key={user.id}>
                          <tr className="border-b border-gray-700/50 hover:bg-gray-800/30">
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                                  <User className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <p className="text-white font-medium">{user.email}</p>
                                  <p className="text-gray-500 text-xs">ID: {user.id}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-2">
                                {user.email_verified ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 text-green-400" />
                                    <span className="text-green-400 text-sm">Verified</span>
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-4 h-4 text-red-400" />
                                    <span className="text-red-400 text-sm">Unverified</span>
                                  </>
                                )}
                              </div>
                              {user.is_admin && (
                                <div className="flex items-center space-x-1 mt-1">
                                  <Shield className="w-3 h-3 text-purple-400" />
                                  <span className="text-purple-400 text-xs">Admin</span>
                                </div>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-2">
                                {user.plan === 'paid' ? (
                                  <>
                                    <Crown className="w-4 h-4 text-yellow-400" />
                                    <span className="text-yellow-400 text-sm font-medium">Premium</span>
                                  </>
                                ) : (
                                  <span className="text-gray-400 text-sm">Free</span>
                                )}
                              </div>
                              {user.plan_purchased_at && (
                                <p className="text-gray-500 text-xs mt-1">
                                  Since {new Date(user.plan_purchased_at).toLocaleDateString()}
                                </p>
                              )}
                            </td>
                            <td className="py-4 px-4">
  <div className="space-y-1">
    <div className="flex items-center space-x-2">
      <MessageSquare className="w-3 h-3 text-blue-400" />
      <span className="text-gray-300 text-xs">
        {user.chat_count || 0} chats
      </span>
    </div>
    <div className="flex items-center space-x-2">
      <Image className="w-3 h-3 text-green-400" />
      <span className="text-gray-300 text-xs">
        {user.image_count || 0} images
      </span>
    </div>
    <div className="flex items-center space-x-2">
      <Video className="w-3 h-3 text-orange-400" />
      <span className="text-gray-300 text-xs">
        {user.video_count || 0} videos
      </span>
    </div>
  </div>
  {/* Optional: Show free tier usage in a smaller subtext */}
  <div className="mt-1 text-xs text-gray-500">
    <span>Free used: {user.free_chats_used || 0}c / {user.free_images_used || 0}i / {user.free_videos_used || 0}v</span>
  </div>
</td>
                            <td className="py-4 px-4">
  <p className="text-white text-sm">{new Date(user.created_at).toLocaleDateString()}</p>
  <p className="text-gray-500 text-xs">{formatRelativeTime(user.created_at)}</p>
  {user.last_activity && user.last_activity !== '2000-01-01' && (
    <p className="text-xs text-blue-400 mt-1">
      Last: {formatRelativeTime(user.last_activity)}
    </p>
  )}
</td>
                            <td className="py-4 px-4">
                              <div className="flex flex-col space-y-2">
                                <button
                                  onClick={() => toggleUserExpand(user.id)}
                                  className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs rounded-lg transition-colors"
                                >
                                  {expandedUser === user.id ? (
                                    <>
                                      <ChevronUp className="w-3 h-3" />
                                      <span>Hide</span>
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-3 h-3" />
                                      <span>View</span>
                                    </>
                                  )}
                                </button>
                                {user.plan !== 'paid' && (
                                  <button
                                    onClick={() => handleMakePremium(user.id)}
                                    className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded-lg transition-colors"
                                  >
                                    <Crown className="w-3 h-3" />
                                    <span>Upgrade</span>
                                  </button>
                                )}
                                {!user.email_verified && (
                                  <button
                                    onClick={() => handleVerifyEmail(user.id)}
                                    className="flex items-center justify-center space-x-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors"
                                  >
                                    <CheckCircle className="w-3 h-3" />
                                    <span>Verify</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          
                          {/* Expanded User Details */}
                          {expandedUser === user.id && userDetails && (
                            <tr className="bg-gray-900/50">
                              <td colSpan="6" className="p-6">
                                <div className="space-y-6">
                                  {/* User Stats */}
<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
  <div className="bg-gray-800/50 rounded-lg p-4">
  <p className="text-gray-400 text-sm mb-1">Last Activity</p>
  <p className="text-lg font-bold text-white">
    {userDetails.last_activity && userDetails.last_activity !== '2000-01-01'
      ? new Date(userDetails.last_activity).toLocaleDateString()
      : 'No activity'}
  </p>
  <p className="text-xs text-gray-400 mt-1">
    {userDetails.last_activity && userDetails.last_activity !== '2000-01-01'
      ? formatRelativeTime(userDetails.last_activity)
      : ''}
  </p>
</div>
  <div className="bg-gray-800/50 rounded-lg p-4">
    <p className="text-gray-400 text-sm mb-1">Account Age</p>
    <p className="text-2xl font-bold text-white">
      {Math.max(0, Math.floor((new Date() - new Date(userDetails.created_at)) / (1000 * 60 * 60 * 24)))} days
    </p>
    <p className="text-xs text-gray-400 mt-2">
      Joined: {new Date(userDetails.created_at).toLocaleDateString()}
    </p>
  </div>
  <div className="bg-gray-800/50 rounded-lg p-4">
    <p className="text-gray-400 text-sm mb-1">Last Activity</p>
    <p className="text-lg font-bold text-white">
      {userChats[0]?.updated_at 
        ? new Date(userChats[0].updated_at).toLocaleDateString()
        : userImages[0]?.created_at
        ? new Date(userImages[0].created_at).toLocaleDateString()
        : userVideos[0]?.created_at
        ? new Date(userVideos[0].created_at).toLocaleDateString()
        : 'No activity'}
    </p>
    <p className="text-xs text-gray-400 mt-1">
      {userChats[0]?.updated_at 
        ? formatRelativeTime(userChats[0].updated_at)
        : userImages[0]?.created_at
        ? formatRelativeTime(userImages[0].created_at)
        : userVideos[0]?.created_at
        ? formatRelativeTime(userVideos[0].created_at)
        : ''}
    </p>
  </div>
</div>

                                  {/* Chat Sessions */}
                                  <div>
                                    <div className="flex items-center justify-between mb-3">
                                      <h3 className="text-white font-semibold flex items-center space-x-2">
                                        <MessageSquare className="w-4 h-4 text-blue-400" />
                                        <span>Chat Sessions</span>
                                      </h3>
                                      <span className="text-xs text-gray-400">
                                        {userChats.length} sessions
                                      </span>
                                    </div>
                                    <div className="space-y-2">
                                      {userChats.slice(0, 5).map((chat) => (
                                        <div key={chat.id} className="bg-gray-800/30 rounded-lg p-3">
                                          <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                              <p className="text-white text-sm font-medium">{chat.title}</p>
                                              <div className="flex items-center space-x-3 mt-1 text-xs text-gray-400">
                                                <span>{chat.message_count} messages</span>
                                                <span>{chat.user_messages} user · {chat.assistant_messages} assistant</span>
                                                <span>{formatRelativeTime(chat.updated_at)}</span>
                                              </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <button
                                                onClick={() => setExpandedChat(expandedChat === chat.id ? null : chat.id)}
                                                className="p-1.5 text-gray-400 hover:text-white bg-gray-700 rounded-lg"
                                              >
                                                <Eye className="w-4 h-4" />
                                              </button>
                                              <button
                                                onClick={() => handleDeleteContent(user.id, 'chat', chat.id)}
                                                className="p-1.5 text-red-400 hover:text-red-300 bg-gray-700 rounded-lg"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </button>
                                            </div>
                                          </div>
                                          
                                          {expandedChat === chat.id && (
                                            <div className="mt-3 pt-3 border-t border-gray-700">
                                              <a
                                                href={`${API_URL}/chat/download/${chat.id}?format=txt`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-purple-400 hover:text-purple-300"
                                              >
                                                Download Chat
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Image Generations */}
                                  <div>
                                    <div className="flex items-center justify-between mb-3">
                                      <h3 className="text-white font-semibold flex items-center space-x-2">
                                        <Image className="w-4 h-4 text-green-400" />
                                        <span>Image Generations</span>
                                      </h3>
                                      <span className="text-xs text-gray-400">
                                        {userImages.length} images
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                      {userImages.slice(0, 5).map((image) => (
                                        <div key={image.id} className="relative group">
                                          <img
                                            src={image.image_url}
                                            alt={image.prompt}
                                            className="w-full h-24 object-cover rounded-lg cursor-pointer"
                                            onClick={() => {
                                              setSelectedContent(image);
                                              setShowContentModal(true);
                                            }}
                                          />
                                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                                            <button
                                              onClick={() => handleDeleteContent(user.id, 'image', image.id)}
                                              className="p-1.5 bg-red-600 rounded-lg"
                                            >
                                              <Trash2 className="w-4 h-4 text-white" />
                                            </button>
                                          </div>
                                          {image.approved === 0 && (
                                            <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-yellow-600 text-white text-xs rounded">
                                              Pending
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>

                                  {/* Video Generations */}
                                  {userVideos.length > 0 && (
                                    <div>
                                      <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-white font-semibold flex items-center space-x-2">
                                          <Video className="w-4 h-4 text-orange-400" />
                                          <span>Video Generations</span>
                                        </h3>
                                        <span className="text-xs text-gray-400">
                                          {userVideos.length} videos
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        {userVideos.slice(0, 3).map((video) => (
                                          <div key={video.id} className="bg-gray-800/30 rounded-lg p-3">
                                            <p className="text-white text-sm truncate">{video.prompt}</p>
                                            <div className="flex items-center justify-between mt-2">
                                              <span className="text-xs text-gray-400">
                                                {new Date(video.created_at).toLocaleDateString()}
                                              </span>
                                              <button
                                                onClick={() => handleDeleteContent(user.id, 'video', video.id)}
                                                className="p-1.5 text-red-400 hover:text-red-300 bg-gray-700 rounded-lg"
                                              >
                                                <Trash2 className="w-4 h-4" />
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center p-6 border-t border-gray-700">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white"
                  >
                    Previous
                  </button>
                  <span className="text-gray-400">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
            
            {activeTab === 'chat-usage' && <AdminChatUsage />}

            {/* MODERATION TAB */}
            {activeTab === 'moderation' && (
              <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
                <div className="p-6 border-b border-gray-700">
                  <h2 className="text-xl font-bold text-white mb-2">Content Moderation Queue</h2>
                  <p className="text-gray-400">Review and moderate user-generated content</p>
                </div>
                
                <div className="divide-y divide-gray-700">
                  {moderationQueue.length === 0 ? (
                    <div className="p-12 text-center">
                      <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No content pending moderation</p>
                    </div>
                  ) : (
                    moderationQueue.map((item) => (
                      <div key={item.id} className="p-6 hover:bg-gray-800/30">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="px-2 py-1 bg-purple-600/20 text-purple-400 text-xs rounded-full">
                                {item.content_type}
                              </span>
                              <span className="text-gray-400 text-xs">
                                {formatRelativeTime(item.created_at)}
                              </span>
                            </div>
                            
                            <p className="text-white mb-2">{item.prompt}</p>
                            
                            {item.content_type === 'image' && (
                              <img
                                src={item.image_url}
                                alt={item.prompt}
                                className="max-h-48 rounded-lg mb-3"
                              />
                            )}
                            
                            <div className="flex items-center space-x-2 text-xs text-gray-400">
                              <User className="w-3 h-3" />
                              <span>{item.user_email}</span>
                              <span>ID: {item.user_id}</span>
                            </div>
                          </div>
                          
                          <div className="flex space-x-2 ml-4">
                            <button
                              onClick={() => handleModerateImage(item.id, true)}
                              className="flex items-center space-x-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                            >
                              <ThumbsUp className="w-4 h-4" />
                              <span>Approve</span>
                            </button>
                            <button
                              onClick={() => handleModerateImage(item.id, false)}
                              className="flex items-center space-x-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                            >
                              <ThumbsDown className="w-4 h-4" />
                              <span>Reject</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                {/* Pagination */}
                {moderationQueue.length > 0 && (
                  <div className="flex justify-between items-center p-6 border-t border-gray-700">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white"
                    >
                      Previous
                    </button>
                    <span className="text-gray-400">
                      Page {page} of {totalPages}
                    </span>
                    <button
                      onClick={() => setPage(p => p + 1)}
                      disabled={page >= totalPages}
                      className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white"
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* ANALYTICS TAB */}
            {activeTab === 'analytics' && usageAnalytics && (
              <div className="space-y-6">
                {/* Usage Trend */}
                <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <TrendingUp className="w-5 h-5 text-purple-400" />
                    <span>Usage Trends</span>
                  </h2>
                  
                  <div className="h-64 flex items-center justify-center">
                    <p className="text-gray-400">
                      Chart visualization would go here - use a charting library like recharts
                    </p>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-3 gap-4 text-center">
                    <div className="p-3 bg-gray-900/50 rounded-lg">
                      <p className="text-gray-400 text-sm">Chats</p>
                      <p className="text-xl font-bold text-white">
                        {usageAnalytics.usage_trend.filter(t => t.feature === 'chats')
                          .reduce((sum, t) => sum + t.count, 0)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-900/50 rounded-lg">
                      <p className="text-gray-400 text-sm">Images</p>
                      <p className="text-xl font-bold text-white">
                        {usageAnalytics.usage_trend.filter(t => t.feature === 'images')
                          .reduce((sum, t) => sum + t.count, 0)}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-900/50 rounded-lg">
                      <p className="text-gray-400 text-sm">Videos</p>
                      <p className="text-xl font-bold text-white">
                        {usageAnalytics.usage_trend.filter(t => t.feature === 'videos')
                          .reduce((sum, t) => sum + t.count, 0)}
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Top Users */}
                <div className="bg-gray-800/50 rounded-lg border border-gray-700 p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
                    <Award className="w-5 h-5 text-yellow-400" />
                    <span>Top Users by Activity</span>
                  </h2>
                  
                  <div className="space-y-3">
                    {usageAnalytics.top_users.map((user, index) => (
                      <div key={user.id} className="flex items-center justify-between p-3 bg-gray-900/30 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <span className="w-6 h-6 flex items-center justify-center bg-gray-700 rounded-full text-xs font-bold text-white">
                            {index + 1}
                          </span>
                          <div>
                            <p className="text-white text-sm">{user.email}</p>
                            <p className="text-gray-400 text-xs">ID: {user.id}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-white text-sm font-medium">
                              {user.total_chats + user.total_images + user.total_videos}
                            </p>
                            <p className="text-gray-400 text-xs">total items</p>
                          </div>
                          {user.plan === 'paid' && (
                            <Crown className="w-4 h-4 text-yellow-400" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ACTIVITY LOG TAB */}
            {activeTab === 'activity' && (
              <div className="bg-gray-800/50 rounded-lg border border-gray-700 overflow-hidden">
                <div className="p-6 border-b border-gray-700">
                  <h2 className="text-xl font-bold text-white mb-2">Admin Activity Log</h2>
                  <p className="text-gray-400">Track all administrative actions</p>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700 bg-gray-900/50">
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Time</th>
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Admin</th>
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Action</th>
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Target</th>
                        <th className="py-4 px-4 text-left text-gray-400 text-sm font-medium">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLogs.map((log) => (
                        <tr key={log.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                          <td className="py-4 px-4">
                            <p className="text-white text-sm">{new Date(log.created_at).toLocaleString()}</p>
                            <p className="text-gray-500 text-xs">{formatRelativeTime(log.created_at)}</p>
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-white text-sm">{log.admin_email}</p>
                            <p className="text-gray-500 text-xs">ID: {log.admin_id}</p>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              log.action.includes('UPGRADE') ? 'bg-yellow-600/20 text-yellow-400' :
                              log.action.includes('DELETE') ? 'bg-red-600/20 text-red-400' :
                              log.action.includes('VERIFY') ? 'bg-green-600/20 text-green-400' :
                              'bg-blue-600/20 text-blue-400'
                            }`}>
                              {log.action}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            {log.target_type && (
                              <div>
                                <p className="text-white text-sm">{log.target_type}</p>
                                <p className="text-gray-500 text-xs">ID: {log.target_id}</p>
                              </div>
                            )}
                          </td>
                          <td className="py-4 px-4">
                            <p className="text-gray-300 text-sm">
                              {log.details ? JSON.stringify(log.details).substring(0, 50) : '-'}
                              {log.details && JSON.stringify(log.details).length > 50 && '...'}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                <div className="flex justify-between items-center p-6 border-t border-gray-700">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white"
                  >
                    Previous
                  </button>
                  <span className="text-gray-400">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Content Preview Modal */}
      {showContentModal && selectedContent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
          <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">Content Preview</h3>
              <button
                onClick={() => setShowContentModal(false)}
                className="p-2 text-gray-400 hover:text-white rounded-lg"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <img
                src={selectedContent.image_url}
                alt={selectedContent.prompt}
                className="max-w-full max-h-[60vh] mx-auto rounded-lg"
              />
              <div className="mt-4">
                <p className="text-gray-400 text-sm">Prompt</p>
                <p className="text-white">{selectedContent.prompt}</p>
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center space-x-4 text-xs text-gray-400">
                    <span>Model: {selectedContent.model}</span>
                    <span>Created: {new Date(selectedContent.created_at).toLocaleString()}</span>
                  </div>
                  <button
                    onClick={() => {
                      handleDeleteContent(selectedContent.user_id, 'image', selectedContent.id);
                      setShowContentModal(false);
                    }}
                    className="flex items-center space-x-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;